
// =============================================================
//  audioImpulse.js -- stochastic repeating process
// =============================================================

import {
  createAudioOverlay,
  formatAudioImpulseOverlay,
  parseOverlayLevel,
  selectFromPool,
  audioBufferCache,
  sharedAudioCtx,
  fmtNum,
  fetchAudioBuffer,
  ensureAudioExtension,
  buildAudioInfoHeader,
  getPlaybackClientsFromEl,
  nextBidirectionalSpeed,
  makePatternGenerator,
  valueNow,
  takeFollows,
  steerFollows,
} from "./audioShared.js";

import { handleAudioCue, consoleBox } from "./audioFile.js";
import { isAuthoredExtentGroup, truthy } from "../../parser/cueUtils.js";
import { sendOSC } from "../../system/oscillaOSCClient.js";
import { ensureAudioPool } from "./audioPool.js";
import { playheadScreenX } from "../../system/paths.js";
import { registerTask, completeTask } from "../../system/loadTasks.js";
import { secs } from "../beats.js";
import {
  renderWaveform,
  getWaveform,
  addCursor,
  startCursor,
  removeCursor,
  removeAllCursors,
  addPeakLayer,
  removePeakLayer,
  removeAllPeakLayers,
  updateWaveformLayout
} from "../../system/waveform.js";
import { shuffle } from "./patterns.js";

// =============================================================
//  Impulse state
// =============================================================

const audioImpulses = window.audioImpulses || (window.audioImpulses = new Map());
window.audioImpulses = audioImpulses;

// =============================================================
//  Pattern generator for speed/pitch sequences
// =============================================================

/**
 * Build a step generator from a raw param value.
 * Handles pattern objects, arrays (as Pseq), and constants.
 */
function buildSpeedGenerator(raw) {
  if (raw == null) return null;

  // Pattern object (from parser)
  if (typeof raw === "object" && raw.type === "pattern") {
    return makePatternGenerator(raw);
  }

  // Array shorthand -> Pseq
  if (Array.isArray(raw) && raw.length > 0) {
    return makePatternGenerator({ name: "Pseq", values: raw, repeats: "inf" });
  }

  // Constant or rand - no generator needed
  return null;
}

/**
 * Format speed/pitch pattern display with current value highlighted.
 * Returns HTML string with bold red current value.
 */
function formatSpeedPattern(gen, currentValue) {
  if (!gen?.values?.length) {
    return `speed:${fmtNum(currentValue)}`;
  }

  const name = gen.name || "Pseq";
  const highlighted = gen.values.map(v => {
    const num = Number(v);
    const isActive = Math.abs(num - currentValue) < 0.001;
    return isActive
      ? `<span style="color:#c00;font-weight:bold;pointer-events:none">${fmtNum(v)}</span>`
      : fmtNum(v);
  }).join(",");

  return `pitch:${name}(${highlighted})`;
}

// =============================================================
//  Interval computation
// =============================================================

function computeInterval(rate, jitter = 0) {
  if (!rate || rate <= 0) return 2.0;

  const base = 60 / rate; // events per minute -> seconds between hits

  if (!jitter || jitter <= 0) return base;

  // jitter: 0 -> stable, 1 -> fully random (0..2x)
  const min = Math.max(0.01, base * (1 - jitter));
  const max = base * (1 + jitter);

  return min + Math.random() * (max - min);
}

// =============================================================
//  Pool helper (reuses audioPool cache)
// =============================================================

async function ensureImpulsePool(uid, params) {
  return ensureAudioPool(uid, params);
}

// =============================================================
//  Scheduling
// =============================================================

/**
 * rate: / jitter: following a signal: the process takes each new value for
 * its next interval. A rate turned up a long way does not wait out the gap
 * already scheduled at the old one: a hit due later than the new interval
 * would put it is brought forward.
 */
function steerImpulse(state, follows) {
  state._unsteer?.();
  state._unsteer = null;
  if (!follows || !Object.keys(follows).length) return;
  state._unsteer = steerFollows(follows, (v) => {
    if (state.stopped) return;
    Object.assign(state, v);
    if (v.rate != null && state.timer && state.rate > 0) {
      const left = (state._dueAt ?? 0) - performance.now();
      if (left > (60 / state.rate) * 1000) {
        clearTimeout(state.timer);
        scheduleNextImpulse(state);
      }
    }
  });
}

function scheduleNextImpulse(state) {
  if (state.stopped) return;

  const interval = computeInterval(state.rate, state.jitter);
  state._dueAt = performance.now() + interval * 1000;

  state.timer = setTimeout(async () => {
    if (state.stopped) return;

    // During seeking the RAF loop is paused and checkImpulseRegions
    // cannot police region bounds.  Skip the hit but keep scheduling
    // so the process resumes naturally once seeking ends.
    if (window.isSeeking) {
      scheduleNextImpulse(state);
      return;
    }

    try {
      await playImpulseHit(state);
    } catch (err) {
      console.warn("[audioImpulse] play failed:", err);
    }

    scheduleNextImpulse(state);
  }, interval * 1000);
}

// =============================================================
//  Single hit playback
// =============================================================

async function playImpulseHit(state) {
  const { uid, params, pool } = state;

  if (!pool?.files?.length) return;

  // For region-lifetime impulses that have already been confirmed inside,
  // verify playhead is still inside the region. This prevents voice
  // accumulation during seeking/rewind when the timer keeps firing but
  // the playhead has moved outside the bounding box.
  // Skip this check until _wasInsideOnce is set -- before that, the
  // edge-crossing detector in checkCueTriggers is the authority.
  if (state.lifetime === "region" && state._regionEl && state._wasInsideOnce) {
    const playX = playheadScreenX();
    if (playX != null) {
      // Use parent group's bbox if element is inside a group (for SVG-authored extent lines)
      // But NOT for audio objects on the interaction layer
      let regionEl = state._regionEl;
      const isGroupHost = regionEl.tagName?.toLowerCase() === 'g';
    const hasDslExtent = regionEl._dslExtentW > 0;
    const parent = regionEl.parentElement;
      if (!isGroupHost && !hasDslExtent && isAuthoredExtentGroup(parent, regionEl)) {
        regionEl = parent;
      }

      const rect = regionEl.getBoundingClientRect();
      const containerRect = window.scoreContainer?.getBoundingClientRect();
      const rectLeft = rect.left - (containerRect?.left || 0);
      let rectRight = rect.right - (containerRect?.left || 0);


      // DSL extent:N — boundary computed live from the element's current bbox
      if (state._regionEl._dslExtentW > 0) {
        const scale = state._regionEl.parentElement?.getScreenCTM?.()?.a;
        if (scale) {
          rectRight = (state._regionEl.getBoundingClientRect().left - (containerRect?.left || 0)) + state._regionEl._dslExtentW * scale;
        }
      }

      if (playX < rectLeft - 5 || playX > rectRight + 5) {
        return;
      }
    }
  }

  // Select file from pool (shared logic with audioPool)
  const file = selectFromPool(pool);
  if (!file) return;

  // Resolve base values (DSL-aware): a follow()/map() is read as it is now,
  // so each hit takes the signal at the moment it fires
  const baseAmp = valueNow(params.amp) ?? 1;
  const basePan = valueNow(params.pan) ?? 0;

  // Speed/pitch: use pattern generator if available, otherwise eval
  let baseSpeed;
  if (state._speedGen) {
    const nextVal = state._speedGen.next();
    baseSpeed = nextVal != null ? Number(nextVal) : 1;
  } else {
    baseSpeed = valueNow(params.speed ?? params.pitch) ?? 1;
  }

  const panRandom = Number(params.panRandom ?? 0);
  const reverse = params.reverse ?? false;
  const bidirectional = params.bidirectional ?? false;

  // Centered per-hit randomisation
  const randAround = (base, range, min = -Infinity, max = Infinity) => {
    if (!range || range <= 0) return base;
    const delta = (Math.random() * 2 - 1) * range;
    const v = base + delta;
    return Math.max(min, Math.min(max, v));
  };

  const amp = Math.max(0, Math.min(1, baseAmp));
  const pan = randAround(basePan, panRandom, -1, 1);

  // Speed calculation based on pitch mode (none, range, pattern)
  const pitchMode = params.pitchMode ?? "none";
  const oldPitchRandom = Number(params.speedRandom ?? params.pitchRandom ?? 0);

  let speed;

  // Backward compatibility: convert old pitchRandom to range mode
  if (oldPitchRandom > 0 && pitchMode === "none") {
    if (bidirectional) {
      const magnitude = Math.max(0.01, baseSpeed + oldPitchRandom - 1);
      speed = (Math.random() * 2 - 1) * magnitude;
    } else if (reverse) {
      const lo = -(baseSpeed + oldPitchRandom);
      const hi = -Math.max(0.01, baseSpeed - oldPitchRandom);
      speed = lo + Math.random() * (hi - lo);
    } else {
      speed = randAround(baseSpeed, oldPitchRandom);
      speed = Math.max(0.01, speed);
    }
  } else if (pitchMode === "range") {
    // Range mode: pick random value between min and max
    const min = params.pitchMin ?? 0.8;
    const max = params.pitchMax ?? 1.2;
    if (bidirectional) {
      // 50/50 chance of positive or negative range
      const useNegative = Math.random() < 0.5;
      speed = min + Math.random() * (max - min);
      if (useNegative) speed = -speed;
    } else if (reverse) {
      speed = -(min + Math.random() * (max - min));
    } else {
      speed = min + Math.random() * (max - min);
    }
  } else if (pitchMode === "pattern") {
    // Pattern mode: use pattern generator (via state._speedGen if set, or create one)
    // For impulse, we use the speed generator stored in state
    if (state._speedPatternGen) {
      let val = state._speedPatternGen.next();
      if (val === null) {
        state._speedPatternGen.reset();
        val = state._speedPatternGen.next() ?? 1;
      }
      if (bidirectional) val = nextBidirectionalSpeed(uid, Math.abs(val));
      else if (reverse && val > 0) val = -Math.abs(val);
      speed = val;
    } else {
      // Fallback if no pattern generator
      speed = reverse ? -baseSpeed : baseSpeed;
    }
  } else {
    // "none" mode - static speed; bidirectional alternates direction
    // per hit (forward, reverse, forward, ...).
    if (bidirectional) {
      speed = nextBidirectionalSpeed(uid, baseSpeed);
    } else {
      speed = reverse ? -baseSpeed : baseSpeed;
    }
  }

  // Avoid zero zone for bidirectional
  if (bidirectional && Math.abs(speed) < 0.01) {
    speed = speed >= 0 ? 0.01 : -0.01;
  }

  // fadeIn: / fadeOut: as the inspector writes them, fadein: / fadeout: as
  // the docs do; fade: is both
  const fadeIn = params.fadeIn ?? params.fadein ?? params.fade ?? 0;
  const fadeOut = params.fadeOut ?? params.fadeout ?? params.fade ?? 0;

  // Build cue
  const filename = params.path
    ? `${params.path}/${file}`
    : file;

  const poly = Number(params.poly ?? 1);

  // Enforce polyphony limit: count active voices for this impulse
  const reg = window.activeAudioCues;
  if (reg && poly > 0) {
    const activeKeys = [];
    for (const key of reg.keys()) {
      if (key === uid || key.startsWith(`${uid}__`)) {
        activeKeys.push(key);
      }
    }
    if (activeKeys.length >= poly) {
      // At capacity -- skip this hit, wait for a voice to free up
      return;
    }
  }

  let playUid = uid;
  if (poly > 1) {
    playUid = `${uid}__${Date.now()}_${Math.floor(Math.random() * 9999)}`;
  }

  const cue = {
    type: "cueAudio",
    src: filename,
    uid: playUid,
    amp,
    pan,
    speed,
    fadeIn,
    fadeOut,
    loop: 1,
    toggle: false,
    waveform: "none",  // audioImpulse handles its own waveform via peak layers
    _box: state._box,  // ... in its console box, if it has one
    // Effects pass-through
    filter: params.filter ?? null,
    delay: params.delay ?? null,
    reverb: params.reverb ?? null
  };

  await handleAudioCue(cue);

  // osc:1 / oscaddr: each hit goes out as OSC, as audioPool's does
  // (server.js osc_audio_impulse: /oscilla/audio/impulse, or the oscaddr:)
  if (truthy(params.osc) || params.oscaddr) {
    sendOSC({
      type: "osc_audio_impulse",
      uid,
      filename,
      amp,
      pan,
      speed,
      fadeIn,
      fadeOut,
      addr: params.oscaddr || null
    });
  }

  // --- Per-hit waveform cursor and peak layer ---
  // Lazy lookup: primeImpulseWaveform is async and may not have
  // completed when the impulse process first starts (e.g. page
  // reload with playhead already inside the rect). Re-check on
  // each hit so we pick up the handle once priming finishes.
  if (!state._waveformHandle) {
    state._waveformHandle = getWaveform(`wf-impulse-${uid}`) || null;
  }
  const wfHandle = state._waveformHandle;
  const ctx = sharedAudioCtx;
  const audioFilename = ensureAudioExtension(cue.src);
  const buf = audioBufferCache.get(audioFilename);

  if (wfHandle && ctx && buf) {
    // Update header with current filename
    if (wfHandle._headerDiv) {
      const headerStr = buildAudioInfoHeader({
        audioObjectType: params.audioObjectType || "audioImpulse",
        audioObjectLabel: params.audioObjectLabel,
        filename: file,
        playbackClients: getPlaybackClientsFromEl(state._regionEl)
      });
      wfHandle._headerDiv.textContent = headerStr;
    }

    // Update details text with resolved per-hit params
    // Respect visibility state - don't show details if toggle is off
    if (wfHandle._detailsDiv) {
      const parts = [];
      parts.push(`amp:${fmtNum(amp)}`);
      // Show pattern with highlighted current value if using pattern generator
      // Use baseSpeed for pattern highlighting (before random offset applied)
      if (state._speedGen) {
        parts.push(formatSpeedPattern(state._speedGen, baseSpeed));
      } else {
        parts.push(`speed:${fmtNum(speed)}`);
      }
      parts.push(`pan:${fmtNum(pan)}`);
      parts.push(`rate:${fmtNum(state.rate)}`);
      if (state.jitter) parts.push(`jitter:${fmtNum(state.jitter)}`);
      // Use innerHTML to support styled pattern display
      wfHandle._detailsDiv.innerHTML = parts.join(" | ");
      // Ensure visibility respects the handle's state
      wfHandle._detailsDiv.style.display = wfHandle._labelVisible ? "" : "none";
      if (wfHandle._labelVisible) updateWaveformLayout(wfHandle);
    } else if (wfHandle._infoText) {
      // Fallback for waveforms without split header/details
      const parts = [];
      parts.push(`amp:${fmtNum(amp)}`);
      if (state._speedGen) {
        parts.push(formatSpeedPattern(state._speedGen, baseSpeed));
      } else {
        parts.push(`speed:${fmtNum(speed)}`);
      }
      parts.push(`pan:${fmtNum(pan)}`);
      parts.push(`rate:${fmtNum(state.rate)}`);
      if (state.jitter) parts.push(`jitter:${fmtNum(state.jitter)}`);
      wfHandle._infoText.innerHTML = parts.join(" | ");
      updateWaveformLayout(wfHandle);
    }

    // On first live hit, clear the primed preview layers
    if (!state._primedCleared) {
      state._primedCleared = true;
      removeAllPeakLayers(wfHandle);
    }

    // Add peak layer for this file (unique per voice)
    addPeakLayer(wfHandle, buf, audioFilename, { id: playUid });

    // Get the layer's color so cursor matches
    const layerColor = wfHandle._peakLayers?.get(playUid)?.color || "#c00";

    // Remove any existing cursor with same id (mono mode: poly=1)
    removeCursor(wfHandle, playUid);

    const subCursor = addCursor(wfHandle, playUid, {
      color: layerColor, width: "1.6", opacity: "0.6"
    });

    if (subCursor) {
      startCursor(subCursor, ctx, ctx.currentTime, buf.duration, speed);

      // Auto-remove cursor and peak layer when this voice ends
      const onStop = (e) => {
        if (e.detail?.uid === playUid && e.detail?.state === "stop") {
          removeCursor(wfHandle, playUid);
          removePeakLayer(wfHandle, playUid);
          window.removeEventListener("oscilla:audio", onStop);
        }
      };
      window.addEventListener("oscilla:audio", onStop);
    }
  }
}

// =============================================================
//  handleAudioImpulseCue
// =============================================================

export async function handleAudioImpulseCue(ast, el, opts = {}) {
  try {
    const params = ast.params || ast;

    const {
      path,
      glob,
      format = "wav",
      mode = "shuffle",

      rate = 30,
      jitter = 0,

      poly = 6,

      uid = `impulse-${path || "pool"}`,

      pan
    } = params;

    if (!path) {
      console.warn("[audioImpulse] Missing path:", params);
      return;
    }

    // rate: and jitter: may follow() a signal (resolved here, steered by
    // steerImpulse once the process exists)
    const follows = takeFollows({ rate, jitter });

    // If already running, hot-update params instead of ignoring
    if (audioImpulses.has(uid)) {
      const existing = audioImpulses.get(uid);
      const oldPath = existing.params.path;
      existing.rate = Number(follows.rate ? follows.rate.now : rate);
      existing.jitter = Number(follows.jitter ? follows.jitter.now : jitter);
      steerImpulse(existing, follows);
      existing.params = { ...existing.params, ...params, poly };
      // a new speed: (a pattern or a number) takes the old pattern's place
      const speedRaw = params.speed ?? params.pitch;
      if (JSON.stringify(speedRaw) !== JSON.stringify(existing._speedRaw)) {
        existing._speedRaw = speedRaw;
        existing._speedGen = buildSpeedGenerator(speedRaw);
      }

      // If path changed, invalidate pool cache and rebuild
      if (path !== oldPath) {
        const poolCache = window.audioPools;
        if (poolCache) poolCache.delete(uid);

        // Clear pool to suppress hits while rebuilding
        existing.pool = { files: [], mode: existing.pool?.mode || "shuffle", cursor: 0 };

        ensureImpulsePool(uid, { path, glob, format: params.format, mode }).then(pool => {
          if (pool?.files?.length) {
            existing.pool = pool;
          }
        }).catch(err => {
          console.warn(`[audioImpulse] Pool rebuild failed:`, err);
        });
      }

      // Update waveform details text (header stays constant)
      // Respect visibility state - don't show details if toggle is off
      const wfHandle = existing._waveformHandle;
      if (wfHandle?._detailsDiv || wfHandle?._infoText) {
        const parts = [];
        if (path) parts.push(`path:${path}`);
        if (rate) parts.push(`rate:${fmtNum(rate)}`);
        if (jitter) parts.push(`jitter:${fmtNum(jitter)}`);
        if (poly) parts.push(`poly:${poly}`);
        if (pan != null && pan !== 0) parts.push(`pan:${fmtNum(pan)}`);
        const detailsStr = parts.join(" | ");

        if (wfHandle._detailsDiv) {
          wfHandle._detailsDiv.textContent = detailsStr;
          // Ensure visibility respects the handle's state
          wfHandle._detailsDiv.style.display = wfHandle._labelVisible ? "" : "none";
        } else if (wfHandle._infoText) {
          wfHandle._infoText.textContent = detailsStr;
        }
        if (wfHandle._labelVisible) updateWaveformLayout(wfHandle);
        existing._infoBase = detailsStr;
      }
      return;
    }

    const pool = await ensureImpulsePool(uid, { path, glob, format: params.format, mode });

    if (!pool?.files?.length) {
      console.warn("[audioImpulse] Empty pool:", params);
      return;
    }

    // Create overlay (persistent while running) -- skip if waveform provides info
    const wfHandle = getWaveform(`wf-impulse-${uid}`);
    let overlay = null;
    if (el && !wfHandle) {
      overlay = createAudioOverlay({
        anchorEl: el,
        label: `${path}`
      });
      overlay.update("starting...");
      overlay.position();
    }

    // Build speed/pitch pattern generator if applicable
    const speedRaw = params.speed ?? params.pitch;
    const speedGen = buildSpeedGenerator(speedRaw);

    // Build pitch mode pattern generator (pitchMode field)
    let speedPatternGen = null;
    if (params.pitchMode === "pattern" && params.pitchValues?.length) {
      speedPatternGen = makePatternGenerator({
        name: params.pitchPattern ?? "Prand",
        values: params.pitchValues,
        repeats: Infinity
      });
    }

    // Build complete state object
    const state = {
      uid,
      params: { ...params, poly },
      rate: Number(follows.rate ? follows.rate.now : rate),
      jitter: Number(follows.jitter ? follows.jitter.now : jitter),
      pool,
      stopped: false,
      timer: null,

      // Speed/pitch pattern support
      _speedGen: speedGen,
      _speedRaw: speedRaw,
      _speedPatternGen: speedPatternGen,

      // Region lifetime support
      _regionEl: el || null,
      lifetime: params.lifetime || "process",

      _overlay: overlay
    };

    // From the console: a box of its own, the waveforms in it
    if (!el) state._box = await consoleBox(ast, uid, primeImpulseWaveform);

    // Look up primed waveform handle (from primeImpulseWaveform)
    state._waveformHandle = getWaveform(`wf-impulse-${uid}`) || null;

    // Store base info string for dynamic file updates
    const infoParts = [`audioImpulse`];
    if (path) infoParts.push(`path:${path}`);
    if (params.rate) infoParts.push(`rate:${fmtNum(params.rate)}`);
    if (params.jitter) infoParts.push(`jitter:${fmtNum(params.jitter)}`);
    if (params.poly) infoParts.push(`poly:${params.poly}`);
    if (params.pan != null && params.pan !== 0) infoParts.push(`pan:${fmtNum(params.pan)}`);
    state._infoBase = infoParts.join(" | ");

    audioImpulses.set(uid, state);
    steerImpulse(state, follows);

    // Play first hit immediately
    playImpulseHit(state);

    // Then schedule repeating process
    scheduleNextImpulse(state);

  } catch (err) {
    console.error("[audioImpulse] error:", err);
  }
}

// =============================================================
//  checkImpulseRegions (called from RAF loop)
// =============================================================

export function checkImpulseRegions() {
  if (!window.audioImpulses) return;

  const playX = playheadScreenX();
  if (playX == null) return;

  for (const state of window.audioImpulses.values()) {
    if (!state) continue;
    if (state.lifetime !== "region") continue;
    if (!state._regionEl) continue;

    // Use parent group's bbox if element is inside a group (for SVG-authored extent lines)
    // But NOT for audio objects on the interaction layer
    let regionEl = state._regionEl;
    const isGroupHost = regionEl.tagName?.toLowerCase() === 'g';
    const hasDslExtent = regionEl._dslExtentW > 0;
    const parent = regionEl.parentElement;
    if (!isGroupHost && !hasDslExtent && isAuthoredExtentGroup(parent, regionEl)) {
      regionEl = parent;
    }

    // Get fresh bounds every tick
    const rect = regionEl.getBoundingClientRect();
    const containerRect = window.scoreContainer?.getBoundingClientRect();

    const rectLeft = rect.left - (containerRect?.left || 0);
    let rectRight = rect.right - (containerRect?.left || 0);


    // DSL extent:N — boundary computed live from the element's current bbox
    if (state._regionEl._dslExtentW > 0) {
      const scale = state._regionEl.parentElement?.getScreenCTM?.()?.a;
      if (scale) {
        rectRight = (state._regionEl.getBoundingClientRect().left - (containerRect?.left || 0)) + state._regionEl._dslExtentW * scale;
      }
    }

    const EPS = 1.5;

    let entryTolerance = EPS;
    if (state._tickCount === undefined) state._tickCount = 0;
    state._tickCount++;

    if (state._tickCount < 10) {
      entryTolerance = 50;
    }

    const inside = !(playX < rectLeft - entryTolerance || playX > rectRight + EPS);

    // On state change
    if (state._lastInside !== inside) {
      state._lastInside = inside;

      // Only exit after we've been "inside" at least once
      if (!inside && !state.stopped && state._wasInsideOnce) {
        state.stopped = true;
        const relTime = secs(state.params?.release ?? state.params?.rel ?? 0.3);   // beats (beats.js)
        stopAudioImpulse(state.uid, relTime);
      }

      // Track if we've ever been inside (only after tolerance stabilises)
      if (inside && state._tickCount >= 10) {
        state._wasInsideOnce = true;
      }
    }
  }
}

// =============================================================
//  stopAudioImpulse with release fadeout support
// =============================================================

export function stopAudioImpulse(uid, releaseSec = 0) {
  const st = audioImpulses.get(uid);
  if (!st) return;

  st.stopped = true;
  st._unsteer?.(); st._unsteer = null;

  // Stop scheduling new hits
  if (st.timer) {
    clearTimeout(st.timer);
    st.timer = null;
  }

  // Get release time from params if not provided
  const fadeTime = releaseSec || secs(st.params?.release || st.params?.rel || 0.3);

  // Remove all per-hit waveform cursors and peak layers
  if (st._waveformHandle) {
    removeAllCursors(st._waveformHandle);
    removeAllPeakLayers(st._waveformHandle);
    // Restore base contour visibility (shows first file's waveform)
    const handle = st._waveformHandle;
    if (handle.upperLine) handle.upperLine.setAttribute("opacity", "1");
    if (handle.lowerLine) handle.lowerLine.setAttribute("opacity", "1");
  }

  // Fade out any currently playing audio from this impulse
  const reg = window.activeAudioCues;
  if (reg && fadeTime > 0) {
    for (const [key, voice] of reg.entries()) {
      if (key === uid || key.startsWith(`${uid}__`)) {
        try {
          voice.stop?.(fadeTime);
        } catch (e) {
          console.warn(`[audioImpulse] fadeout failed for ${key}:`, e);
        }
      }
    }
  }

  // Remove from map immediately so retrigger can create a fresh entry.
  // The captured `st` object keeps delayed cleanup closures working.
  audioImpulses.delete(uid);

  // Destroy overlay after fade completes
  if (st._overlay) {
    const overlay = st._overlay;
    setTimeout(() => {
      overlay?.destroy();
    }, fadeTime * 1000 + 50);
  }
}

// =============================================================
//  stopAllAudioImpulses
// =============================================================

export function stopAllAudioImpulses() {
  if (!window.audioImpulses) return;

  for (const uid of window.audioImpulses.keys()) {
    stopAudioImpulse(uid);
  }
}


// =============================================================
//  Prime audioImpulse overlay (called during assignCues)
// =============================================================

export function primeAudioImpulseOverlay(ast, cueElement) {
  if (!ast || !cueElement) return;

  const params = ast.params || ast;
  const overlayLevel = parseOverlayLevel(params.overlay ?? ast.overlay);
  if (overlayLevel <= 0) return;

  // Skip HTML overlay if waveform display is active (it has its own info text)
  const waveformParam = params.waveform ?? "self";
  if (waveformParam !== "none" && waveformParam !== "0") return;

  if (cueElement._audioImpulseOverlayPrimed) return;
  cueElement._audioImpulseOverlayPrimed = true;

  const overlay = createAudioOverlay({
    anchorEl: cueElement,
    label: "audioImpulse",
    track: true
  });

  if (overlay?.el) {
    const text = formatAudioImpulseOverlay(params, overlayLevel);
    overlay.update(text);
    overlay.position();
    cueElement._audioImpulseOverlay = overlay;
    cueElement._audioImpulseOverlayLevel = overlayLevel;
  }
}

// =============================================================
//  Prime impulse waveform (called during assignCues)
//  Fetches pool, decodes first file's buffer, renders waveform.
// =============================================================

export async function primeImpulseWaveform(ast, cueElement) {
  if (!ast || !cueElement) return;

  const params = ast.params || ast;
  const waveformParam = params.waveform ?? "self";

  if (waveformParam === "none" || waveformParam === "0") return;
  if (cueElement._impulseWaveformPrimed) return;
  cueElement._impulseWaveformPrimed = true;

  const svg = cueElement.ownerSVGElement;
  if (!svg) return;

  const {
    path,
    glob,
    format = "wav",
    mode = "shuffle",
    uid = `impulse-${path || "pool"}`
  } = params;

  if (!path) return;

  // Register task SYNCHRONOUSLY before any async work
  // This ensures the progress indicator waits for impulse waveform loading
  const taskId = `impulse-waveform:${uid}`;
  if (!window._audioTaskRegistered) window._audioTaskRegistered = new Set();
  if (!window._audioTaskRegistered.has(taskId)) {
    window._audioTaskRegistered.add(taskId);
    registerTask(taskId, `Impulse: ${path}`);
  }

  try {
    const pool = await ensureImpulsePool(uid, { path, glob, format: params.format, mode });
    if (!pool?.files?.length) return;

    const ctx = sharedAudioCtx;

    // Helper to fetch + decode + cache a pool file
    const loadFile = async (file) => {
      let filename = path ? `${path}/${file}` : file;
      // Only append format if file has no recognized audio extension
      const hasAudioExt = /\.(wav|mp3|ogg|webm|flac|aac|m4a|aiff?)$/i.test(filename);
      if (!hasAudioExt) filename = `${filename}.${format}`;

      const buf = await fetchAudioBuffer(ctx, filename);
      return { filename, buf };
    };

    // Render base waveform group with first file (black contours)
    const first = await loadFile(pool.files[0]);

    // Build header (always visible) and details (toggleable) separately
    const headerStr = buildAudioInfoHeader({
      audioObjectType: params.audioObjectType || "audioImpulse",
      audioObjectLabel: params.audioObjectLabel,
      filename: first.filename,
      playbackClients: getPlaybackClientsFromEl(cueElement)
    });
    const detailsParts = [];
    if (path) detailsParts.push(`path:${path}`);
    if (params.rate) detailsParts.push(`rate:${fmtNum(params.rate)}`);
    if (params.jitter) detailsParts.push(`jitter:${fmtNum(params.jitter)}`);
    if (params.poly) detailsParts.push(`poly:${params.poly}`);
    if (params.pan != null && params.pan !== 0) detailsParts.push(`pan:${fmtNum(params.pan)}`);
    const detailsStr = detailsParts.join(" | ");

    const handle = renderWaveform(svg, waveformParam, first.buf, `wf-impulse-${uid}`, first.filename, {
      element: cueElement,
      header: headerStr,
      info: detailsStr
    });

    if (!handle) return;

    // Hide base contour -- audioImpulse uses peak layers exclusively
    if (handle.upperLine) handle.upperLine.setAttribute("opacity", "0");
    if (handle.lowerLine) handle.lowerLine.setAttribute("opacity", "0");

    // Pick up to 3 random files as preview layers
    const shuffled = shuffle(pool.files);
    const previewFiles = shuffled.slice(0, Math.min(3, shuffled.length));

    for (const file of previewFiles) {
      try {
        const { filename, buf } = await loadFile(file);
        addPeakLayer(handle, buf, filename, { opacity: "0.75" });
      } catch (err) {
        console.warn(`[audioImpulse] Preview layer failed for ${file}:`, err);
      }
    }

    // Preload remaining pool files in background (non-blocking)
    // so they're cached before first trigger
    const previewSet = new Set(previewFiles);
    const remaining = pool.files.filter(f => !previewSet.has(f));
    if (remaining.length > 0) {
      const preloadBatch = async () => {
        for (const file of remaining) {
          try { await loadFile(file); } catch (err) { console.warn(`[audioImpulse] Preload failed for ${file}:`, err); }
        }
      };
      preloadBatch(); // fire-and-forget
    }
  } catch (err) {
    console.warn(`[audioImpulse] Waveform prime failed:`, err);
  } finally {
    // Complete the task to update progress indicator
    completeTask(taskId);
    window._audioTaskRegistered?.delete(taskId);
  }
}
